import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { PlacementType } from "@/types/inboxPlacements.types";
import { SITE_BASE_URL } from "@/lib/constants";

interface PublishLinksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  links?: string[];
  type: "email-health" | "inbox-placement";
  placementReportType?: PlacementType;
}


const EMAIL_HEALTH_ROUTE = "shared-report";
const INBOX_PLACEMENT_ROUTE = "shared-inboxplacement-report";

export function PublishLinksDialog({
  open,
  onOpenChange,
  links = [],
  type,
  placementReportType,
}: PublishLinksDialogProps) {
  const [copying, setCopying] = useState(false);

  const handleCopyLink = async (link: string) => {
    try {
      setCopying(true);
      const fullLink =
        type === "email-health"
          ? `${SITE_BASE_URL}/${EMAIL_HEALTH_ROUTE}/${link}`
          : `${SITE_BASE_URL}/${INBOX_PLACEMENT_ROUTE}/${link}?type=${placementReportType}`;
      await navigator.clipboard.writeText(fullLink);
      toast.success("Link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy link");
    } finally {
      setCopying(false);
    }
  };

  const handleExportLinks = () => {
    if (!links.length) return;

    const fullLinks = links.map((link) => {
      if (type === "email-health") {
        return `${SITE_BASE_URL}/${EMAIL_HEALTH_ROUTE}/${link}`;
      } else {
        return `${SITE_BASE_URL}/${INBOX_PLACEMENT_ROUTE}/${link}?type=${placementReportType}`;
      }
    });
    const csvContent = "data:text/csv;charset=utf-8," + fullLinks.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "published-links.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const prefix =
    type === "email-health"
      ? `${SITE_BASE_URL}/${EMAIL_HEALTH_ROUTE}`
      : `${SITE_BASE_URL}/${INBOX_PLACEMENT_ROUTE}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Published Links</DialogTitle>
        </DialogHeader>
        <div className="max-h-[300px] overflow-y-auto">
          {links.length > 0 ? (
            <ul className="space-y-2">
              {links.map((link, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                >
                  <a
                    href={`${prefix}/${link}${
                      placementReportType ? `?type=${placementReportType}` : ""
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline truncate mr-2"
                  >
                    {`${prefix}/${link}${
                      placementReportType ? `?type=${placementReportType}` : ""
                    }`}
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyLink(link)}
                    disabled={copying}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center text-gray-500 py-4">
              No published links available
            </p>
          )}
        </div>
        <DialogFooter className="">
          <Button
            variant="outline"
            onClick={handleExportLinks}
            disabled={!links.length}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export as CSV
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
